/**
 * Confluence-owned renderer for the Invited App Shortcuts GRS recommendations
 * query (productionisation cohort — PUMA-2011).
 *
 * Confluence does NOT use the AFM entry-points contract (Jira's PUMA-2000 does).
 * Instead it follows the same SSR pattern as `preloadPlatformNavigationQuery`:
 *
 *   1. `preloadInvitedAppShortcutsRecommendationsQuery` `fetchQuery`s this
 *      operation at route resolution time (SSR), populating the Relay store.
 *   2. This renderer reads the SAME operation + variables with
 *      `useLazyLoadQuery({ fetchPolicy: 'store-or-network' })`, so the
 *      SSR-preloaded record is served synchronously with no client round-trip.
 *
 * The `graphql` template below is kept aligned with
 * `CANONICAL_INVITED_APP_SHORTCUTS_RECOMMENDATIONS_QUERY`
 * (`@atlassian/cross-join-nudges/.../invited-app-shortcuts-recommendations-provider/canonical-query`)
 * so the shared translator's response contract holds.
 *
 * The raw AGG response is handed verbatim to the shared pure translator
 * `buildInvitedAppShortcutsRecommendationsResult` so the discriminated result
 * type stays a single source of truth across Jira and Confluence.
 */
import React, { Suspense, useEffect, useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { isAllowedTargetAppForInvitedAppShortcuts } from '@atlassian/cross-join-nudges/common/utils/invited-app-shortcuts-utils';
import { useInvitedAppShortcuts } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import type { InvitedAppShortcutAllowedTargetAppKey } from '@atlassian/cross-join-nudges/types';
import { buildInvitedAppShortcutsRecommendationsResult } from '@atlassian/cross-join-nudges/ui/invited-app-shortcuts/invited-app-shortcuts-recommendations-provider';
import type {
	ShortcutsConfig,
	ShortcutsSlotProps,
} from '@atlassian/global-side-navigation/common/types';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expVal } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { getInvitedAppShortcutsCache } from './hooks/useResolveInvitedAppShortcuts.tsx';
import { buildInvitedAppShortcutsRecommendationsVariables } from './invitedAppShortcutsRecommendationsVariables.ts';
import type { InvitedAppShortcutsRecommendationsQuery } from './__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

const SOURCE_APP = 'confluence' as const;

/**
 * The Relay operation. Kept as a module-level constant so both this renderer
 * and the drift test can reference the same document.
 */
export const InvitedAppShortcutsRecommendationsQueryNode = graphql`
	query InvitedAppShortcutsRecommendationsQuery(
		$tenantId: ID
		$product: String!
		$rerank: [GrowthRecRerankCandidate!]
	) {
		growthRecommendations {
			recommendations(
				context: { tenantId: $tenantId, product: $product, useCase: "invitedAppShortcuts" }
				rerank: $rerank
			) {
				__typename
				... on GrowthRecRecommendations {
					data {
						entityId
						reasons
						score
					}
					context {
						tenantId
						product
						useCase
						sessionId
					}
				}
				... on QueryError {
					identifier
					message
					extensions {
						statusCode
						errorType
					}
				}
			}
		}
	}
`;

export type InvitedAppShortcutsRecommendationsProps = {
	/**
	 * The pre-bound GSN shortcuts menu handed to us by the `ShortcutsSlot`. We
	 * render it with the decorated `referrableShortcuts` items.
	 */
	Component: ShortcutsSlotProps['Component'];
	cloudId: string;
	/** Base shortcuts config reconstructed from the slot's resolved items. */
	shortcutsConfig: ShortcutsConfig;
	userId: string;
};

/**
 * Suspends on a cold Relay store (first paint before the SSR cache hydrates),
 * so it MUST be rendered inside a `<Suspense>` boundary by the caller.
 */
export function InvitedAppShortcutsRecommendations({
	Component,
	cloudId,
	shortcutsConfig,
	userId,
}: InvitedAppShortcutsRecommendationsProps): React.JSX.Element {
	// Memoised so referential equality is stable across re-renders — otherwise useLazyLoadQuery
	// treats it as a new request and refetches. Built from the shared builder so the values stay
	// identical to the preloader's (a mismatch would miss the SSR-seeded store record).
	const variables = useMemo(
		() => buildInvitedAppShortcutsRecommendationsVariables(cloudId),
		[cloudId],
	);

	const data = useLazyLoadQuery<InvitedAppShortcutsRecommendationsQuery>(
		InvitedAppShortcutsRecommendationsQueryNode,
		variables,
		// store-or-network: serve the SSR-preloaded record synchronously; do not
		// fire a second network request when the store is already populated.
		{ fetchPolicy: 'store-or-network' },
	);

	// Map the translated recommendations into the typed allowed-target list.
	// `error` and `empty` results both fall through to `[]`, which the
	// productionisation hook treats as "no recommended apps" — matching control.
	// `data` is the generated `$data` type, structurally assignable to the translator's response
	// contract (both derive from the canonical selection set) — no cast needed.
	const recommendedApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey> = useMemo(() => {
		const result = buildInvitedAppShortcutsRecommendationsResult(data);
		if (result.kind !== 'recommendations') {
			return [];
		}
		return result.recommendations.reduce<InvitedAppShortcutAllowedTargetAppKey[]>((acc, rec) => {
			if (isAllowedTargetAppForInvitedAppShortcuts(rec.entityId)) {
				acc.push(rec.entityId);
			}
			return acc;
		}, []);
	}, [data]);

	// Reuse the SAME process-singleton cookie cache as the experiment branch.
	// Productionisation does not read the cache for eligibility (that comes from
	// `recommendedApps`), but the hook's "active in both modes" writes still keep
	// the cache warm as a fallback against cohort flips / gate-off. Do NOT pass
	// `traits` / `onCacheNeedsRefresh` — those are experiment-mode inputs only.
	const resolverResult = useInvitedAppShortcuts({
		enabled: true,
		mode: 'productionisation',
		sourceProduct: SOURCE_APP,
		targetProducts: recommendedApps,
		cloudId,
		userId,
		cache: getInvitedAppShortcutsCache(),
		recommendedApps,
	});

	// Fire the productionisation experiment exposure only when there is an actual recommendation to
	// show — i.e. when the cohort user is genuinely shown the treatment. Cohort routing
	// (PlatformNavigationComponent) evaluates with UNSAFE_expValEqualsNoExposure, so the real
	// exposure is logged here: `expVal` fires the Statsig exposure and `fireFeatureExposed` emits the
	// product analytics event, mirroring the experiment cohort's exposure firing.
	useEffect(() => {
		if (recommendedApps.length > 0) {
			resolverResult.fireFeatureExposed({
				isEnabled: expVal('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', false),
			});
		}
	}, [recommendedApps, resolverResult]);

	return (
		<InvitedAppShortcutsContainer
			mode="productionisation"
			cloudId={cloudId}
			userId={userId}
			recommendedApps={recommendedApps}
			sourceApp={SOURCE_APP}
			shortcutsConfig={shortcutsConfig}
			resolverResult={resolverResult}
		>
			{({ referrableShortcuts }) => (
				<Component shortcutItems={Object.values(referrableShortcuts)} />
			)}
		</InvitedAppShortcutsContainer>
	);
}

type FailOpenBoundaryProps = {
	children: React.ReactNode;
	/** Rendered if the recommendations subtree throws — the default shortcuts menu. */
	fallback: React.ReactNode;
};

/**
 * Fails the productionisation recommendations subtree open to the default
 * shortcuts menu. `useLazyLoadQuery` throws on a network/parse error above the
 * container's own error boundary, so we need a boundary here to keep the nav
 * rendering (control-cohort parity) rather than crashing.
 */
class FailOpenBoundary extends React.Component<FailOpenBoundaryProps, { hasError: boolean }> {
	static getDerivedStateFromError() {
		return { hasError: true };
	}

	constructor(props: FailOpenBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error: Error) {
		// Report through the structured monitoring client (which scrubs/handles error payloads) rather
		// than logging the raw error, then mark it handled since we fail open to the default menu.
		getMonitoringClient().submitError(error, { attribution: Attribution.GROWTH_PUMA });
		markErrorAsHandled(error);
	}

	render() {
		return this.state.hasError ? this.props.fallback : this.props.children;
	}
}

/**
 * Slot-facing wrapper: scopes Suspense (cold Relay store) and the fail-open
 * ErrorBoundary to the shortcuts subtree. Both fall back to the pre-bound
 * `Component` (the default shortcuts menu) so a cold cache or a failed GraphQL
 * request never blocks the rest of the nav.
 */
export function SafeInvitedAppShortcutsRecommendations(
	props: InvitedAppShortcutsRecommendationsProps,
): React.JSX.Element {
	const { Component } = props;
	const fallback = <Component />;
	return (
		<Suspense fallback={fallback}>
			<FailOpenBoundary fallback={fallback}>
				<InvitedAppShortcutsRecommendations {...props} />
			</FailOpenBoundary>
		</Suspense>
	);
}
